import { Injectable, Inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AppPreferencesService } from './app-preferences';

interface TimestampParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

interface ParsedDisplayTimestamp extends TimestampParts {
  style: 'ad' | 'bs';
}

@Injectable({
  providedIn: 'root',
})
export class ServerTimeService {
  private readonly serverTimeState = signal<Date | null>(null);
  private readonly serverTimeBsState = signal<string | null>(null);
  private timerId: number | null = null;
  private readonly localDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/;
  private readonly timezoneDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:\d{2})$/;
  private readonly displayDateTimePattern = /^(\d{2})-(\d{2})-(\d{2,4})\s+(\d{2}):(\d{2}):(\d{2})$/;
  private readonly bsDisplayDateTimePattern = /^([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/;

  constructor(
    private readonly preferencesSvc: AppPreferencesService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  initialize(currentTime: string | Date | null | undefined, currentTimeBs: string | null | undefined = null): void {
    const parsedTime = this.parseCurrentTime(currentTime);
    if (!parsedTime) {
      return;
    }

    this.serverTimeState.set(parsedTime);
    this.serverTimeBsState.set(this.normalizeBsTimeLabel(currentTimeBs, parsedTime));
    this.startTimer();
  }

  reset(): void {
    this.stopTimer();
    this.serverTimeState.set(null);
    this.serverTimeBsState.set(null);
  }

  getCurrentTime(): Date | null {
    const value = this.serverTimeState();
    return value ? new Date(value.getTime()) : null;
  }

  getCurrentTimeLabel(): string {
    const value = this.serverTimeState();
    if (!value) {
      return '';
    }

    if (this.preferencesSvc.showBsDate) {
      return this.serverTimeBsState() ?? this.preferencesSvc.formatCurrentTimeLabel(value);
    }

    return this.preferencesSvc.formatCurrentTimeLabel(value);
  }

  getCurrentBsDateValue(): string | null {
    const value = this.serverTimeBsState();
    if (!value) {
      return null;
    }

    const parsed = this.parseDisplayTimestamp(value);
    if (!parsed || parsed.style !== 'bs') {
      return null;
    }

    return [
      String(parsed.year).padStart(4, '0'),
      String(parsed.month).padStart(2, '0'),
      String(parsed.day).padStart(2, '0'),
    ].join('-');
  }

  private startTimer(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.stopTimer();
    this.timerId = window.setInterval(() => {
      const currentValue = this.serverTimeState();
      if (!currentValue) {
        return;
      }

      this.serverTimeState.set(new Date(currentValue.getTime() + 1000));

      const currentBsValue = this.serverTimeBsState();
      if (currentBsValue) {
        this.serverTimeBsState.set(this.advanceTimestampLabel(currentBsValue));
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerId !== null && isPlatformBrowser(this.platformId)) {
      window.clearInterval(this.timerId);
    }

    this.timerId = null;
  }

  private parseCurrentTime(currentTime: string | Date | null | undefined): Date | null {
    if (currentTime === null || currentTime === undefined || currentTime === '') {
      return null;
    }

    if (currentTime instanceof Date) {
      return new Date(currentTime.getTime());
    }

    const trimmed = currentTime.trim();
    if (!trimmed) {
      return null;
    }

    const timezoneMatch = this.timezoneDateTimePattern.exec(trimmed);
    if (timezoneMatch) {
      const [
        ,
        year,
        month,
        day,
        hour,
        minute,
        second = '00',
        fraction = '',
        timeZone,
      ] = timezoneMatch;

      const milliseconds = Number((fraction + '000').slice(0, 3));
      return new Date(
        `${year}-${month}-${day}T${hour}:${minute}:${second}.${String(milliseconds).padStart(3, '0')}${timeZone}`,
      );
    }

    const localMatch = this.localDateTimePattern.exec(trimmed);
    if (localMatch) {
      const [
        ,
        year,
        month,
        day,
        hour,
        minute,
        second = '00',
        fraction = '',
      ] = localMatch;

      const milliseconds = Number((fraction + '000').slice(0, 3));
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        milliseconds,
      );
    }

    const fallback = new Date(trimmed.replace(' ', 'T'));
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  private normalizeBsTimeLabel(currentTimeBs: string | null | undefined, fallbackTime: Date): string {
    const normalized = typeof currentTimeBs === 'string' ? currentTimeBs.trim() : '';
    if (normalized) {
      return normalized;
    }

    return this.preferencesSvc.formatCurrentTimeLabel(fallbackTime);
  }

  private advanceTimestampLabel(label: string): string {
    const parsed = this.parseDisplayTimestamp(label);
    if (!parsed) {
      return label;
    }

    const date = new Date(
      parsed.style === 'bs'
        ? new Date(parsed.year, parsed.month - 1, parsed.day, parsed.hour, parsed.minute, parsed.second)
        : new Date(
            Date.UTC(
              parsed.year,
              parsed.month - 1,
              parsed.day,
              parsed.hour,
              parsed.minute,
              parsed.second,
            ),
          ),
    );
    if (parsed.style === 'bs') {
      date.setSeconds(date.getSeconds() + 1);
      return this.formatDisplayTimestamp(
        {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate(),
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
        },
        'bs',
      );
    }

    date.setUTCSeconds(date.getUTCSeconds() + 1);
    return this.formatDisplayTimestamp({
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
      second: date.getUTCSeconds(),
    }, 'ad');
  }

  private parseDisplayTimestamp(label: string): ParsedDisplayTimestamp | null {
    const normalized = typeof label === 'string' ? label.trim() : '';
    if (!normalized) {
      return null;
    }

    const match = this.displayDateTimePattern.exec(normalized);
    if (match) {
      const year = Number(match[3].length === 2 ? `20${match[3]}` : match[3]);
      const month = Number(match[2]);
      const day = Number(match[1]);
      const hour = Number(match[4]);
      const minute = Number(match[5]);
      const second = Number(match[6]);

      if (
        Number.isNaN(year) ||
        Number.isNaN(month) ||
        Number.isNaN(day) ||
        Number.isNaN(hour) ||
        Number.isNaN(minute) ||
        Number.isNaN(second)
      ) {
        return null;
      }

      return {
        year,
        month,
        day,
        hour,
        minute,
        second,
        style: 'ad',
      };
    }

    const bsMatch = this.bsDisplayDateTimePattern.exec(normalized);
    if (!bsMatch) {
      return null;
    }

    const month = this.resolveBsMonthNumber(bsMatch[1]);
    const day = Number(bsMatch[2]);
    const year = Number(bsMatch[3]);
    const hour = Number(bsMatch[4]);
    const minute = Number(bsMatch[5]);
    const second = Number(bsMatch[6]);

    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      Number.isNaN(second)
    ) {
      return null;
    }

    return {
      year,
      month,
      day,
      hour,
      minute,
      second,
      style: 'bs',
    };
  }

  private formatDisplayTimestamp(parts: TimestampParts, style: 'ad' | 'bs' = 'ad'): string {
    const year = String(parts.year).padStart(4, '0');
    const timePart = `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}:${String(parts.second).padStart(2, '0')}`;

    if (style === 'bs') {
      return `${this.resolveBsMonthName(parts.month)} ${String(parts.day).padStart(2, '0')}, ${year} ${timePart}`;
    }

    return [
      String(parts.day).padStart(2, '0'),
      String(parts.month).padStart(2, '0'),
      year,
    ].join('-') + ` ${timePart}`;
  }

  private resolveBsMonthNumber(monthName: string): number {
    const normalized = monthName.trim().toLowerCase();
    const monthMap: Record<string, number> = {
      baishakh: 1,
      baisakh: 1,
      jestha: 2,
      jeth: 2,
      asar: 3,
      ashadh: 3,
      ashad: 3,
      shrawan: 4,
      shravan: 4,
      bhadra: 5,
      bhadau: 5,
      aswin: 6,
      ashwin: 6,
      kartik: 7,
      mangsir: 8,
      poush: 9,
      magh: 10,
      falgun: 11,
      phalgun: 11,
      chaitra: 12,
    };

    return monthMap[normalized] ?? 0;
  }

  private resolveBsMonthName(monthNumber: number): string {
    const monthNames = [
      'Baishakh',
      'Jestha',
      'Asar',
      'Shrawan',
      'Bhadau',
      'Aswin',
      'Kartik',
      'Mangsir',
      'Poush',
      'Magh',
      'Falgun',
      'Chaitra',
    ];

    return monthNames[monthNumber - 1] ?? 'Baishakh';
  }
}
